'use client';
import { useEffect, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import type { ButtonProps } from '@/components/atoms/Button';
import { variantColors } from '@/components/atoms/Button/styles';
import { InputFieldProps } from '@/components/atoms/Input';
import { InputLabel } from '@/components/atoms/Input/InputLabel';
import Popover, { PopoverProps } from '@/components/atoms/Popover';
import { SvgIcon } from '@/components/SvgIcon';

import { WithRef } from '@/cms/types/types';

import ImageStrapi, { ImageStrapiProps } from '../Image/ImageStrapi';
import { getSelectedDisplayValue } from '../InputDropdown/utils';
import DropdownListContent from './DropdownListContent';

import iconChevronDownSrc from '~/svg/IconChevronDown.svg';

// Mirrors Button variant='input'; native <button> so the Radix trigger can use asChild.
const inputTriggerStyles = cn(
  'w-fit font-serif font-medium inline-flex gap-2 items-center justify-center rounded-[4px] transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:pointer-events-none',
  variantColors.input.primary,
  'h-10 text-[1rem] lineHeight-[1.5rem] px-4'
);
const sectionStyles = 'flex flex-row items-center justify-center gap-1';

export type DropdownOption = {
  label: string;
  value: string;
  icon?: ImageStrapiProps;
};

export type InputDropdownWithImageProps<T extends DropdownOption> = {
  options: T[];
  onOptionChange: (value: T['value']) => void;
  value?: string;
  hasSearch?: boolean;
  isDisabled?: boolean;
  classNames?: {
    root?: string;
    helpText?: string;
    inputField?: ButtonProps['classNames'];
    dropdown?: string;
  };
  renderListContent?: ({
    handleSelect,
  }: {
    handleSelect: (value: T['value']) => void;
  }) => React.ReactNode;
  renderOption?: ({ option }: { option: T }, index: number) => React.ReactNode;
  renderSelectedValue?: ({ option }: { option: T }) => React.ReactNode;
  popoverProps?: Partial<PopoverProps>;
} & Omit<InputFieldProps, 'classNames'> & {
    targetClassNames?: InputFieldProps['classNames'];
  };

export const InputDropdownWithImage = <T extends DropdownOption>({
  onOptionChange,
  options: propOptions,
  name,
  isDisabled,
  value,
  ref,
  classNames,
  renderOption,
  renderListContent,
  renderSelectedValue,
  ...props
}: WithRef<HTMLInputElement, InputDropdownWithImageProps<T>>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState(propOptions);
  const selectedOption = propOptions.find((option) => option.value === value);
  const { texts } = useTranslation();

  function handleSelect(value: T['value']) {
    onOptionChange(value);
    setIsOpen(false);
  }

  useEffect(() => {
    setOptions([
      { value: '', label: texts.input_dropdown.clear_selection } as T,
      ...propOptions,
    ]);
  }, [propOptions]);

  const selectedDisplayValue = getSelectedDisplayValue({
    value,
    renderSelectedValue,
    options: propOptions,
    placeholder: props.placeholder,
    texts,
  });

  return (
    <div className={cn('relative w-full', classNames?.root)}>
      {props.label && (
        <InputLabel
          label={props.label}
          required={props.isRequired}
          fieldName={name}
          classNames={{
            label: props.targetClassNames?.label?.root,
          }}
        />
      )}
      <div className='align-center relative flex w-full items-center'>
        <Popover
          {...props.popoverProps}
          targetProps={{ asChild: true, ...props.popoverProps?.targetProps }}
          classNames={{
            target: cn(
              'w-full group/input-dropdown',
              props.popoverProps?.classNames?.target
            ),
            childrenWrapper: cn(
              'border-gray-300 border mt-2 py-0 px-0 rounded w-full min-w-[140px]',
              props.popoverProps?.classNames?.childrenWrapper
            ),
          }}
          position='bottom'
          showArrow={false}
          onChange={(value) => {
            if (!isDisabled) setIsOpen(value);
          }}
          isOpen={isDisabled ? false : isOpen}
          contentProps={{
            ...props.popoverProps?.contentProps,
            avoidCollisions: false,
          }}
          target={
            <button
              type='button'
              name={name}
              aria-disabled={isDisabled || undefined}
              className={cn(
                inputTriggerStyles,
                'w-full h-[54px] border data-[state=open]:border-brand-500 data-[state=open]:border-[2px] text-left',
                isDisabled
                  ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                  : '',
                classNames?.inputField?.root
              )}
            >
              {!!selectedOption?.icon && (
                <span
                  className={cn(
                    sectionStyles,
                    classNames?.inputField?.leftSection
                  )}
                >
                  <div className='hidden h-8 w-8 md:block'>
                    <ImageStrapi
                      block={selectedOption.icon}
                      inner={{
                        classNames: { image: 'h-8 w-8 object-contain' },
                      }}
                    />
                  </div>
                </span>
              )}
              <span
                className={cn(
                  'flex h-full items-center justify-center whitespace-nowrap',
                  'h-auto w-full overflow-hidden text-ellipsis inline-block',
                  classNames?.inputField?.inner
                )}
              >
                <div
                  className={cn(
                    'truncate',
                    value
                      ? 'text-gray-950 dark:text-base-dark-white'
                      : 'text-gray-400'
                  )}
                >
                  {selectedDisplayValue}
                </div>
              </span>
              <span
                className={cn(
                  sectionStyles,
                  classNames?.inputField?.rightSection
                )}
              >
                <SvgIcon
                  src={iconChevronDownSrc}
                  className={cn(
                    'ml-auto h-6 w-6 transition duration-500 ease-in-out',
                    isOpen && !isDisabled && 'rotate-180 text-gray-950',
                    isDisabled && 'text-gray-400'
                  )}
                />
              </span>
            </button>
          }
        >
          <div>
            <ul
              className={cn(
                'max-h-80 w-full overflow-y-auto text-gray-950 hover:no-underline',
                classNames?.dropdown,
                isOpen ? 'block' : 'hidden'
              )}
            >
              {!!renderListContent ? (
                renderListContent({ handleSelect })
              ) : (
                <DropdownListContent<T>
                  options={options}
                  handleSelect={handleSelect}
                  renderOption={renderOption}
                  value={value}
                  ref={ref}
                />
              )}
            </ul>
          </div>
        </Popover>
      </div>
    </div>
  );
};
